import { Video } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { ChartCard } from "@/components/waste/reports/chart-card";
import type { Lens } from "@/hooks/use-lens";
import type { WasteLineStats } from "@/hooks/use-line-stats";
import type { TFunction } from "@/i18n/catalog";
import { useTranslation } from "@/i18n/use-translation";
import { formatCurrency, formatWeight } from "@/lib/format";
import { hasPriorHistory, selectWins, type Win } from "@/lib/whats-working";
import { foodLabel } from "@/lib/food-labels";

type Props = {
  /** First line-stats load — show a skeleton, not the empty state. */
  isLoading: boolean;
  lines: WasteLineStats[] | undefined;
  lens: Lens;
  currency: string;
  periodLengthDays: number;
  /** Narrows The Proof feed to the win's line (same wiring as the Pulse strip). */
  onViewEvidence: (win: Win) => void;
};

/**
 * "What's working" (design §9, position 7, POS2-875): sustained wins over the
 * 4-period line stats with the money kept per week — the panel that gives wins
 * equal visual weight to the problem widgets.
 */
export function WhatsWorking({
  isLoading,
  lines,
  lens,
  currency,
  periodLengthDays,
  onViewEvidence,
}: Props) {
  const { locale, t } = useTranslation();
  if (isLoading && lines == null) return <WorkingSkeleton />;

  const result = selectWins(lines ?? [], lens, periodLengthDays);
  const fmt = (v: number) =>
    lens === "weight" ? formatWeight(locale, v) : formatCurrency(locale, v, currency);

  return (
    <ChartCard
      title={t("dashboard.widget.working")}
      description={t("dashboard.workingDescription")}
      isEmpty={result == null}
      emptyLabel={
        hasPriorHistory(lines ?? []) ? t("dashboard.workingEmpty") : t("dashboard.workingNoHistory")
      }
      action={
        result == null ? undefined : (
          <span className="text-xs text-gray-500" data-testid="working-kept-total">
            <span className="font-display text-base font-extrabold tracking-tight text-green-700">
              {fmt(result.totalKeptPerWeek)}
            </span>
            {t("dashboard.workingKeptTotalSuffix")}
          </span>
        )
      }
    >
      {/* Hairline-separated rows per the mock's .winrow (not gaps). */}
      <div className="divide-y divide-gray-100" data-testid="whats-working">
        {result?.wins.map((win) => (
          <WinRow key={win.lineKey} win={win} fmt={fmt} onViewEvidence={onViewEvidence} />
        ))}
      </div>
    </ChartCard>
  );
}

/** The week count is a `_one`/`_other` plural in the catalogue, not an English `s` appended in
 * JavaScript — German pluralises differently and the two-form rule is not a property of numbers
 * (POS2-1158). */
function badgeText(t: TFunction, win: Win): string {
  return win.kind === "hold"
    ? t("dashboard.workingHeld", { count: win.periods })
    : t("dashboard.workingDown", { count: win.periods });
}

function fragmentText(t: TFunction, win: Win): string {
  const pct = Math.round(win.pctDown);
  if (win.kind === "single") return t("dashboard.workingVsLastWeek", { pct });
  return t("dashboard.workingOverWeeks", { count: win.periods, pct });
}

function WinRow({
  win,
  fmt,
  onViewEvidence,
}: {
  win: Win;
  fmt: (v: number) => string;
  onViewEvidence: (win: Win) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-baseline justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
      data-testid="win-row"
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-gray-900" title={foodLabel(t, win.label)}>
          {foodLabel(t, win.label)}
          <span className="ml-1.5 inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 align-[1px] text-[10px] font-bold text-green-700">
            {badgeText(t, win)}
          </span>
        </p>
        <p className="mt-0.5 text-sm text-gray-500">{fragmentText(t, win)}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {win.hasEvidence && (
          <button
            type="button"
            onClick={() => onViewEvidence(win)}
            aria-label={t("dashboard.viewEvidence", { label: foodLabel(t, win.label) })}
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
            data-testid="win-evidence"
          >
            <Video className="size-4" aria-hidden />
          </button>
        )}
        <span className="whitespace-nowrap font-display text-lg font-extrabold tracking-tight tabular-nums text-green-700">
          {fmt(win.keptPerWeek)}
          <span className="ml-0.5 font-sans text-[10.5px] font-semibold text-gray-400">
            {t("dashboard.workingPerWeekKept")}
          </span>
        </span>
      </div>
    </div>
  );
}

function WorkingSkeleton() {
  return (
    <div
      className="rounded-xl border border-gray-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(17,24,39,0.04),0_1px_3px_rgba(17,24,39,0.06)]"
      data-testid="working-skeleton"
    >
      <Skeleton className="h-4 w-1/3" />
      <div className="mt-4 flex flex-col gap-4">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between gap-3">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-5 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}
